"use client";

// next
import { useCallback, useEffect, useState } from "react";

// third party
import cx from "classnames";

// anivive
import { useMount } from "@anivive/hooks";
import { Text } from "@anivive/ui";
import { debounce } from "@anivive/utils";

// components
import GoogleMap from "@/components/ui/google-map";

// services
import { PRACTICES_API_URL } from "@/constants";

// types
import { Practice } from "@/typedef/trial";

// private
import PracticeItem from "./components/item";

// styles
import styles from "./index.module.scss";

type Props = {
  practices?: Practice[];
  enableGeo?: boolean;
  onSelect?: (practice: Practice) => void;
  selected?: { id: number; name: string } | null;
  search?: boolean;
};

export default function StudySiteSelector({
  practices: initialPractices = [],
  enableGeo,
  onSelect,
  selected,
  search,
}: Props) {
  const [geo, setGeo] = useState<{ latitude: number; longitude: number } | null>(null);
  const [practices, setPractices] = useState<Practice[] | null>(initialPractices);
  const [loading, setLoading] = useState(false);
  const [practiceSearch, setPracticeSearch] = useState("");

  useEffect(() => {
    const container = document.getElementById("practice-list");
    const selectedElement = document.getElementById(`marker-${selected}`);
    if (container && selectedElement) {
      container.scrollTo({ top: selectedElement.offsetTop });
    }
  }, [selected]);

  useMount(() => {
    if (enableGeo) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;

          setGeo({ latitude, longitude });
        },
        () => setGeo(null),
      );
    }
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceFetcher = useCallback(
    debounce(async (url: string) => {
      setLoading(true);

      const res = await fetch(url.toString());
      const { data } = await Promise.resolve(res.json());

      if (data) {
        setPractices(data);
      }

      setLoading(false);
    }, 300),
    [],
  );

  const filteredPractices =
    practices?.filter(({ address: { latitude, longitude } }) => latitude && longitude) ?? [];

  const searchPractices = (search: string) => {
    const url = new URL(`/practices?zip=${search}`, PRACTICES_API_URL);
    debounceFetcher(url.toString());
  };

  return (
    <>
      {search && (
        <div className="mb-4">
          <p className="text-style-body1-regular mb-1">Find your primary vet clinic</p>
          <Text
            className="col-12 col-md-6"
            placeholder="Enter Zip Code"
            autoComplete="postal-code"
            name="practiceSearch"
            onChange={(value) => {
              setPracticeSearch(value);
              searchPractices(value);
            }}
            value={practiceSearch}
          />
        </div>
      )}
      {filteredPractices && (
        <div
          className={cx(
            styles["study-site-selector"],
            "d-flex flex-column flex-lg-row justify-content-lg-between",
          )}
        >
          <div className={cx(styles["practice-list"], "order-2 order-lg-1")} id="practice-list">
            {filteredPractices.length > 0
              ? filteredPractices.map((practice, i) => (
                  <PracticeItem
                    key={practice.id}
                    practice={practice}
                    geo={geo}
                    index={i}
                    selected={selected?.id === practice.id}
                    onSelect={(practice) => {
                      if (onSelect) {
                        onSelect(practice);
                      }
                    }}
                  />
                ))
              : !loading &&
                practiceSearch.length === 5 && (
                  <div className="d-flex flex-column align-items-center justify-content-center pt-5">
                    <p className="text-style-body1-regular text-center">
                      Sorry we couldn&apos;t find any matches <br /> for{" "}
                      <span className="weight--600">{search}</span>
                    </p>
                    <small>Please try searching for something else</small>
                  </div>
                )}
          </div>
          <div className={cx(styles.map, "d-none d-md-block position-relative order-1 order-lg-2")}>
            <GoogleMap
              maxZoom={10}
              fitMarkers
              id="study-sites-map"
              markers={filteredPractices.map(({ id, name, address: { latitude, longitude } }) => ({
                latitude,
                longitude,
                info: (
                  <div className="d-flex flex-column align-items-center">
                    <p className="text-center text-style-body2-bold">{name}</p>
                  </div>
                ),
                markerId: `marker-${id}`,
              }))}
              advanced
            />
          </div>
        </div>
      )}
    </>
  );
}
